import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, X } from 'lucide-react';

interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  html_content: string;
  variables: any;
  category: string;
  is_active: boolean;
}

interface EmailTemplatePreviewProps {
  template: EmailTemplate | null;
  isOpen: boolean;
  onClose: () => void;
  onSelect?: () => void;
}

export const EmailTemplatePreview: React.FC<EmailTemplatePreviewProps> = ({
  template,
  isOpen,
  onClose,
  onSelect
}) => {
  if (!template) return null;

  // Create sample data for preview
  const sampleData = {
    company_name: 'TradeBot365',
    company_url: 'https://tradebot365.com',
    support_email: 'support@tradebot365.com',
    user_name: 'John Trader',
    name: 'John Trader',
    date: new Date().toLocaleDateString(),
    time: new Date().toLocaleString(),
    promo_title: 'Summer Trading Boost',
    promo_description: 'Enhance your trading performance with our advanced AI-powered bots',
    promo_details: 'Get 30% off on all premium bot subscriptions',
    maintenance_start: 'July 15, 2024 02:00 UTC',
    maintenance_end: 'July 15, 2024 06:00 UTC',
    impact_description: 'Trading bots will be temporarily unavailable during this period'
  };

  // Replace placeholders with sample data for preview
  let previewContent = template.html_content;
  Object.entries(sampleData).forEach(([key, value]) => {
    const regex = new RegExp(`{{${key}}}`, 'g');
    previewContent = previewContent.replace(regex, String(value));
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Template Preview: {template.name}
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {template.description}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={template.category === 'marketing' ? 'default' : 'secondary'}>
                {template.category}
              </Badge>
              <Button
                variant="ghost"
                size="icon"
                onClick={onClose}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="bg-muted/30 p-4 rounded-lg mb-4 flex-shrink-0">
            <h4 className="font-medium mb-2">Template Variables</h4>
            <div className="flex flex-wrap gap-2">
              {template.variables && Object.keys(template.variables).length > 0 ? (
                Object.keys(template.variables).map((variable) => (
                  <Badge key={variable} variant="outline" className="text-xs">
                    {variable}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">No variables defined</span>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            <div className="border rounded-lg p-6 bg-white min-h-96">
              <div dangerouslySetInnerHTML={{ __html: previewContent }} />
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {onSelect && (
            <Button onClick={() => { onSelect(); onClose(); }}>
              Select This Template
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};